import { useMutation, useQuery } from '@tanstack/react-query';
import { doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import firebaseApp from '../firebase-app';
import { FS_PATH_PUSH_NOTIFICATION_CONTENT_CONNECTION } from '../constants';
import {
  deleteNotification,
  getScheduledNotification,
  schedulePollNotification,
  updateScheduledPollNotification,
} from '../api/push-notification';
import { PushNotificationConnection, SchedulePushdNotificationBody } from '../types/notification';
import moment from 'moment';
import { useUserAuth } from '../contexts/auth';

const getPath = (id: string, abbrv: string) =>
  `${FS_PATH_PUSH_NOTIFICATION_CONTENT_CONNECTION}/${abbrv}/POLL/${id}`;
const getPollConnection = async (params: {
  id: string;
  abbrv: string;
}): Promise<PushNotificationConnection | null> => {
  const { id, abbrv } = params;
  const docs = await getDoc(doc(firebaseApp.db, getPath(id, abbrv)));
  if (docs.exists()) {
    return docs?.data() as PushNotificationConnection;
  }

  return null;
};
export function usePollNotificationConf(params: { abbrv: string; id: string }) {
  const { abbrv, id } = params;
  const { currentUser } = useUserAuth();

  const query = useQuery({
    queryKey: ['poll', 'notification', abbrv, id],
    queryFn: async (): Promise<SchedulePushdNotificationBody | null> => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      const pollConnection = await getPollConnection({
        id,
        abbrv,
      });

      // We save the notification id as array for the future in case we want to support multiple notifications.
      // The id has two parts entity-id and current date
      const pushNotificationId = pollConnection?.pushNotificationIds?.[0];
      if (!pushNotificationId) {
        return null;
      }

      const pollNotification = await getScheduledNotification(abbrv, pushNotificationId, auth);

      return pollNotification || null;
    },
    refetchOnWindowFocus: false,
  });
  const createMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof schedulePollNotification>[0]['body']) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      const notificationRes = await schedulePollNotification({
        abbrv,
        body: {
          notification: payload.notification,
          id: `${id}-${moment().valueOf()}`,
          scheduledTime: payload.scheduledTime,
        },
        auth,
      });
      if (notificationRes.id) {
        const docRef = doc(firebaseApp.db, getPath(id, abbrv));
        await setDoc(docRef, {
          pushNotificationIds: [notificationRes.id],
        });
      }

      return notificationRes;
    },
    onSuccess() {
      query.refetch();
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: Parameters<typeof updateScheduledPollNotification>[0]['body']) => {
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      const notificationRes = await updateScheduledPollNotification({
        abbrv,
        body: {
          notification: payload.notification,
          id: payload.id,
          scheduledTime: payload.scheduledTime,
          createdAt: payload.createdAt,
        },
        auth,
      });
      return notificationRes;
    },
    onSuccess() {
      query.refetch();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (params: { id: string; pollId: string }) => {
      const { id, pollId } = params;
      const auth = await currentUser?.getIdToken();
      if (!auth) {
        return null;
      }
      await deleteNotification(id, auth);

      const docRef = doc(firebaseApp.db, getPath(pollId, abbrv));
      const res = await updateDoc(docRef, {
        pushNotificationIds: null,
      });
      return res;
    },
    onSuccess() {
      query.refetch();
    },
  });
  return {
    query,
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
